import { MARKETS, normaliseMarket } from './markets';

interface DisplayTitleConfig {
  slotId?: string;
}

interface DisplayTemplate {
  id: string;
  name: string;
  language?: string;
  displayTitle?: DisplayTitleConfig;
  displayTitleField?: string;
}

interface DisplayJob {
  templateId?: string;
  name?: string;
  market?: string;
  language?: string;
  props?: Record<string, unknown>;
  inputProps?: Record<string, unknown>;
  assetMappings?: unknown;
}

const TITLE_SEPARATOR = ' – ';

function readTextValue(value: unknown): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (!value || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;
  if (record.type === 'text' && typeof record.value === 'string') {
    const trimmed = record.value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof record.textValue === 'string') {
    const trimmed = record.textValue.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof record.value === 'string') {
    const trimmed = record.value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  return null;
}

function readSlotText(job: DisplayJob, slotId: string): string | null {
  const fromProps = readTextValue(job.props?.[slotId]);
  if (fromProps) return fromProps;

  const fromInputProps = readTextValue(job.inputProps?.[slotId]);
  if (fromInputProps) return fromInputProps;

  const mappings = job.assetMappings;
  if (Array.isArray(mappings)) {
    const match = mappings.find((entry) => {
      if (!entry || typeof entry !== 'object') return false;
      return (entry as Record<string, unknown>).slotId === slotId;
    }) as Record<string, unknown> | undefined;
    return match ? readTextValue(match) : null;
  }

  if (mappings && typeof mappings === 'object') {
    return readTextValue((mappings as Record<string, unknown>)[slotId]);
  }

  return null;
}

function configuredDisplayTitleSlot(template?: DisplayTemplate): string | null {
  const slotId = template?.displayTitle?.slotId ?? template?.displayTitleField;
  if (typeof slotId !== 'string') return null;
  const trimmed = slotId.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function marketLabel(market?: string): string | null {
  if (!market) return null;
  const normalised = normaliseMarket(market);
  if (!normalised || normalised === 'default') return null;
  return MARKETS.find((m) => m.value === normalised)?.label ?? normalised;
}

function templateName(template?: DisplayTemplate, job?: DisplayJob): string {
  const fromTemplate = template?.name?.trim();
  if (fromTemplate) return fromTemplate;
  const fromJobName = job?.name?.trim();
  if (fromJobName) return fromJobName;
  return job?.templateId ?? 'Job';
}

export function getJobDisplayTitle(job: DisplayJob, template?: DisplayTemplate): string {
  const name = templateName(template, job);
  const slotId = configuredDisplayTitleSlot(template);

  if (slotId) {
    const slotText = readSlotText(job, slotId);
    return slotText ? `${name}${TITLE_SEPARATOR}${slotText}` : name;
  }

  // Backward compatibility: preserve existing explicit names when no template title slot is configured.
  const explicitName = job.name?.trim();
  if (explicitName) return explicitName;

  const localeLabel = marketLabel(job.market);
  return localeLabel ? `${name}${TITLE_SEPARATOR}${localeLabel}` : name;
}

